import React from 'react';
import { useTheme } from '@/context/ThemeContext';

// Logo component that switches based on theme
// Note: The SVG files have a 160x160 viewBox but content is horizontally oriented
// We use width constraints instead of height for proper rendering
export function Logo({ variant = 'full', className = '', size = 'default' }) {
  const { isDark } = useTheme();
  
  // For full/wordmark logos, use width-based sizing
  // For symbol-only logos, use height-based sizing
  const getSizeStyle = () => {
    if (variant === 'symbol') {
      const heights = {
        small: 'h-6',
        default: 'h-8',
        large: 'h-10',
        xl: 'h-12',
      };
      return heights[size];
    }
    // Width-based for wordmark logos
    const widths = {
      small: 'w-28',
      default: 'w-36',
      large: 'w-44',
      xl: 'w-52',
    };
    return widths[size];
  };

  const getLogoSrc = () => {
    if (variant === 'symbol') {
      // symbol-dark = dark logo for light backgrounds
      // symbol-white = white logo for dark backgrounds
      return isDark 
        ? '/brand/logo-symbol-dark.svg' 
        : '/brand/logo-symbol-white.svg';
    }
    if (variant === 'two-line') {
      return isDark
        ? '/brand/logo-two-line-gradient-dark.svg'
        : '/brand/logo-two-line-gradient-light.svg';
    }
    // Default: full gradient logo
    // gradient-dark = white/light logo (for dark backgrounds)
    // gradient-white = dark purple logo (for light backgrounds)
    return isDark 
      ? '/brand/logo-gradient-dark.svg' 
      : '/brand/logo-gradient-white.svg';
  };

  return (
    <img 
      src={getLogoSrc()} 
      alt="Secret Sauce"
      className={`${getSizeStyle()} ${variant === 'symbol' ? 'w-auto' : 'h-auto w-auto'} ${className}`}
      data-testid="logo"
    />
  );
}

// Static logo for PDFs and emails (always uses specific variant)
export function StaticLogo({ variant = 'dark', className = '', size = 'default' }) {
  // Determine if this is a symbol or wordmark
  const isSymbol = variant.includes('symbol');
  
  const getSizeStyle = () => {
    if (isSymbol) {
      const heights = {
        small: 'h-6',
        default: 'h-8',
        large: 'h-10',
        xl: 'h-12',
      };
      return heights[size];
    }
    const widths = {
      small: 'w-28',
      default: 'w-36',
      large: 'w-44',
      xl: 'w-52',
    };
    return widths[size];
  };

  const logoMap = {
    'gradient-dark': '/brand/logo-gradient-dark.svg',
    'gradient-white': '/brand/logo-gradient-white.svg',
    'two-colour-dark': '/brand/logo-two-colour-dark.svg',
    'two-colour-white': '/brand/logo-two-colour-white.svg',
    'symbol-dark': '/brand/logo-symbol-dark.svg',
    'symbol-white': '/brand/logo-symbol-white.svg',
    'dark': '/brand/logo-gradient-dark.svg',
    'white': '/brand/logo-gradient-white.svg',
  };

  return (
    <img 
      src={logoMap[variant] || logoMap['dark']} 
      alt="Secret Sauce"
      className={`${getSizeStyle()} ${isSymbol ? 'w-auto' : 'h-auto'} ${className}`}
    />
  );
}
